import React, { memo } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
export function ProductOverview() {
  const items = [{
    icon: '📘',
    title: 'TOME 1: LITTLE EXPLORERS',
    desc: "Ages 3-4 | 50 Printable Pages. The first step in your child's brain training journey. 17 screen-free printable activities carefully designed to build visual recognition, fine motor control, pattern recognition, and emotional awareness — all through pure play.",
    bullets: ['Color the Shapes — visual recognition', 'Trace the Path — motor control', 'Simple Mazes — spatial awareness', 'Shadow Matching — visual processing', 'My Feelings Chart — emotional awareness', 'Trace the Letters A-Z — fine motor + literacy'],
    featured: true
  }, {
    icon: '📗',
    title: 'TOME 2: BRAIN BUILDERS',
    desc: 'Ages 5-6 | 50 Printable Pages. Ready for real mental challenges? 17 activities designed to develop sustained attention, working memory, and early logical thinking.',
    bullets: ['Memory Grid 4x4 — working memory', 'Mirror Drawing — brain hemisphere balance', 'Spot the Difference — attention to detail', 'Code Breaker Symbols — analytical thinking']
  }, {
    icon: '📙',
    title: 'TOME 3: FOCUS WARRIORS',
    desc: "Ages 7-8 | 50 Printable Pages. Screens have shortened your child's attention span. Focus Warriors rebuilds it. 17 powerful activities targeting deep focus and problem-solving.",
    bullets: ['Complex Mazes Level 3 — strategic planning', 'Visual Logic Puzzles — abstract thinking', 'Tangram Challenges — spatial reasoning', 'Sudoku 4x4 — working memory']
  }, {
    icon: '📕',
    title: 'TOME 4: LOGIC MASTERS',
    desc: 'Ages 9-10 | 50 Printable Pages. Your child is ready to think like a strategist. 17 advanced activities targeting critical thinking, verbal reasoning, and cognitive flexibility.',
    bullets: ['Deductive Reasoning Cards — logical deduction', 'Debate Cards — verbal reasoning', 'Cause & Effect Charts — systems thinking', 'Mental Math Sprint — processing speed']
  }, {
    icon: '📒',
    title: 'TOME 5: MEMORY CHAMPIONS',
    desc: 'Ages 8-11 | 50 Printable Pages. Your child studies hard but forgets everything by test day. Memory Champions fixes that. 17 targeted memory training activities.',
    bullets: ['Memory Palace Map — long-term memory', 'Visual Memory Grid 8x8 — photographic memory', 'Flash Card Speed Round — rapid recall', 'The Distraction Challenge — focused recall']
  }, {
    icon: '📓',
    title: 'TOME 6: YOUNG GENIUSES',
    desc: 'Ages 11-12 | 55 Printable Pages. The final level. 15 elite brain challenges developing independent reasoning, creative intelligence, and a leadership mindset.',
    bullets: ['Full Sudoku 9x9 — advanced logic', 'Lateral Thinking Puzzles — creative problem solving', 'Cryptogram Challenge — code and language logic', 'Argument Builder Cards — structured reasoning']
  }];
  const features = ['Ready-to-print pages — zero preparation needed', 'Activities designed for 10 minutes per session', 'Age-appropriate difficulty with progressive levels', 'Clean child-friendly design optimized for home printing', 'Black & white versions included to save on ink', 'Instant digital download — start today'];
  return <section className="bg-white py-16 px-4" data-id="element-164">
      <div className="max-w-6xl mx-auto" data-id="element-165">
        <div className="text-center mb-12" data-id="element-166">
          <h2 className="text-3xl md:text-5xl font-heading font-black text-navy mb-4" data-id="element-167">
            🏆 MEGAPACK NEURO-KID™
          </h2>
          <p className="text-lg md:text-2xl text-orange font-bold mb-2 tracking-wide" data-id="element-168">
            The Complete Screen-Free Brain Training System for Kids Ages 3-12
          </p>
          <p className="text-base md:text-xl text-muted font-medium max-w-3xl mx-auto" data-id="element-169">
            6 Tomes + 5 Bonuses — 300+ Printable Pages
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12" data-id="element-170">
          {items.map((item, idx) => <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.05
        }} key={idx} className={`bg-white rounded-2xl p-8 border-2 border-navy shadow-md hover:shadow-lg transition-shadow ${item.featured ? 'md:col-span-2' : ''}`} data-id="element-171">
              <div className="flex items-start mb-4" data-id="element-172">
                <span className="text-4xl mr-4" data-id="element-173">{item.icon}</span>
                <div data-id="element-174">
                  <h4 className="text-xl font-heading font-bold text-navy leading-tight" data-id="element-175">
                    {item.title}
                  </h4>
                </div>
              </div>
              <p className="text-muted mb-6 text-sm md:text-base font-medium" data-id="element-176">
                {item.desc}
              </p>
              <div className="bg-lightgray rounded-xl p-5 border border-border" data-id="element-177">
                <ul className={`grid grid-cols-1 ${item.featured ? 'md:grid-cols-2' : ''} gap-y-3 gap-x-6`} data-id="element-178">
                  {item.bullets.map((bullet, i) => <li key={i} className="flex items-start text-sm text-ink font-medium" data-id="element-179">
                      <CheckCircle2 className="w-5 h-5 text-green mr-3 shrink-0" data-id="element-180" />
                      {bullet}
                    </li>)}
                </ul>
              </div>
            </motion.div>)}
        </div>

        <div className="bg-navy rounded-2xl p-8 md:p-12 shadow-lg" data-id="element-181">
          <h4 className="text-2xl md:text-3xl font-heading font-bold text-white mb-8 text-center" data-id="element-182">
            Every tome includes:
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto" data-id="element-183">
            {features.map((feature, i) => <div key={i} className="flex items-center text-white bg-white/10 p-4 rounded-lg border border-white/20" data-id="element-184">
                <span className="text-green mr-4 text-xl" data-id="element-185">✅</span>
                <span className="text-sm md:text-base font-medium" data-id="element-186">
                  {feature}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}